import { FC } from 'react';
import { useQuery } from 'react-query';
import { Form } from '@/components/form';
import FormButtons from '@/components/form/form-buttons';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { QueryKey } from '@/lib/query-client';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import { Grid } from '@mui/material';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { cloneDeep } from 'lodash';
import { FieldError } from '@/lib/services';
import { McpBlockActionOptions } from '@/lib/models/policy-model/mcp-controls';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['mcpControls'];
const formModel = new PolicyModel();

interface Props {
  policyId: string;
}

const McpAccessForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy } = useFeature();

  const query = useQuery([QueryKey.ScopedPolicyWithParents, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );
  const { refetch, data: policyData } = query;
  const [policy] = policyData || [];
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      const { patched, error, fieldErrors } = await patchPolicy(
        updateValues.id,
        updateValues.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        refetch();
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { showLoader } = useQueryHelper(query);
  if (showLoader) {
    return;
  }

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection title="MCP Tool Block List" scrollId="blocked-mcp-tools">
        <FormSectionSubtitle>
          Use the table below to explicitly block individual MCP tools.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="mcpControls.toolAccess"
              label="Block List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="mcp-tools-acl"
              aclVariant="block"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="MCP Tool Allow List" scrollId="allowed-mcp-tools">
        <FormSectionSubtitle>
          Use the table below to explicitly allow individual MCP tools.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="mcpControls.toolAccess"
              label="Allow List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="mcp-tools-acl"
              aclVariant="allow"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Allow Read-only">
        <FormSectionSubtitle>
          When this setting is set to "On", any read-only MCP tool not explicitly allowed or blocked
          above will be allowed.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              name="mcpControls.action"
              type="mcp-tool-action-switch"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Catch-all Action">
        <FormSectionSubtitle>
          Choose what action is taken for any MCP tool that is not explicitly allowed or blocked by
          the above settings.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label=""
              name="mcpControls.blockAction"
              type="select"
              options={McpBlockActionOptions}
              formHandle={formHandle}
              readonly={!canChangePolicy}
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default McpAccessForm;
